import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

const rbaColourSelectedSections = new WeakSet();
const rbaGalleryRevisions = new WeakMap();

function getRbaSection(element) {
  const section = element?.closest?.('.shopify-section, dialog');
  if (!section?.querySelector('.rba-tee-picker')) return null;
  if (section.querySelector('[data-rba-crewneck-price-fix-v2]')) return null;
  return section;
}

document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;

    const swatch = event.target.closest('.rba-tee-picker .rba-option--colours .rba-colour-swatch');
    if (!swatch) return;

    const section = getRbaSection(swatch);
    if (section) rbaColourSelectedSections.add(section);
  },
  true
);

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  #replaceRbaGalleryWhenReady(newMediaGallery, section, revision) {
    const firstImage = newMediaGallery.querySelector('slideshow-slide img') || newMediaGallery.querySelector('img');
    const src = firstImage?.getAttribute('src');
    const srcset = firstImage?.getAttribute('srcset');

    const replace = () => {
      if (rbaGalleryRevisions.get(section) !== revision) return;
      this.replaceWith(newMediaGallery);
    };

    if (!src && !srcset) {
      replace();
      return;
    }

    const preloader = new Image();
    if (srcset) preloader.srcset = srcset;
    if (firstImage?.getAttribute('sizes')) preloader.sizes = firstImage.getAttribute('sizes');
    if (src) preloader.src = src;

    if (preloader.complete) {
      replace();
      return;
    }

    preloader.addEventListener('load', replace, { once: true });
    preloader.addEventListener('error', replace, { once: true });
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const rbaSection = getRbaSection(this);

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        if (rbaSection) {
          return;
        }

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        if (rbaSection) {
          const revision = (rbaGalleryRevisions.get(rbaSection) || 0) + 1;
          rbaGalleryRevisions.set(rbaSection, revision);
          this.#replaceRbaGalleryWhenReady(newMediaGallery, rbaSection, revision);
          return;
        }

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
